import { cx } from './hooks';
import { Icon, IconName } from './Icon';

export interface ButtonProps {
  variant?: 'primary' | 'brand' | 'secondary' | 'ghost' | 'danger' | 'danger-text' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  /** Full width. */
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  /** Leading icon (start side). */
  icon?: IconName;
  /** Trailing icon (end side). */
  iconEnd?: IconName;
  /** Renders an <a> when set. */
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: any) => void;
  className?: string;
  children?: any;
  [k: string]: any;
}

/** One primary per screen. primary = coral (main action), brand = violet, secondary = tinted, ghost = text, outline = quiet bordered (use for a second action next to a coral primary), danger = filled destructive (rare), danger-text = text-style destructive (default for cancel/delete in sheets). */
export function Button({ variant = 'primary', size = 'md', block, loading, disabled, icon, iconEnd, href, type = 'button', className, children, onClick, ...rest }: ButtonProps) {
  const cls = cx('pl-btn', `pl-btn--${variant}`, `pl-btn--${size}`, block && 'pl-btn--block', loading && 'is-loading', 'pl-press', className);
  const content = (
    <>
      {loading ? (
        <span className="pl-btn__dots" aria-hidden="true"><i /><i /><i /></span>
      ) : icon ? (
        <Icon name={icon} size={size === 'lg' ? 22 : 20} />
      ) : null}
      <span className="pl-btn__label">{children}</span>
      {iconEnd && !loading ? <Icon name={iconEnd} size={size === 'lg' ? 22 : 20} /> : null}
    </>
  );
  if (href && !disabled) {
    return <a className={cls} href={href} onClick={onClick} {...rest}>{content}</a>;
  }
  const off = disabled || loading;
  return (
    <button className={cls} type={type} disabled={disabled} aria-busy={loading || undefined} aria-disabled={off || undefined} onClick={off ? undefined : onClick} {...rest}>
      {content}
    </button>
  );
}

export interface IconButtonProps {
  icon: IconName;
  /** Required accessible name. */
  label: string;
  variant?: 'plain' | 'tint' | 'brand' | 'accent' | 'hero';
  size?: 'md' | 'lg';
  href?: string;
  onClick?: (e: any) => void;
  disabled?: boolean;
  className?: string;
  [k: string]: any;
}

export function IconButton({ icon, label, variant = 'plain', size = 'md', href, className, disabled, onClick, ...rest }: IconButtonProps) {
  const cls = cx('pl-iconbtn', `pl-iconbtn--${variant}`, size === 'lg' && 'pl-iconbtn--lg', 'pl-press', className);
  const inner = <Icon name={icon} size={size === 'lg' ? 26 : 22} />;
  if (href && !disabled) return <a className={cls} href={href} aria-label={label} {...rest}>{inner}</a>;
  return <button className={cls} type="button" aria-label={label} disabled={disabled} onClick={onClick} {...rest}>{inner}</button>;
}
